import {
  coordinateToMapPoint,
  MapMarkerInput,
  WebMarkerInput,
  MobileMarkerInput,
  VisitEntry
} from "@caredna-marlin/map-wrapper";
import { getShiftStartEndLocation, getVisitResourceLocation } from "../locations";
import { ShiftGeneric } from "../calendarEvents";

interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
}

interface PlaceResult {
  address_components?: GeocoderAddressComponent[];
  place_id?: string;
}

const getAddressComponent = (result: PlaceResult) =>
  (componentType: string, part: "short_name" | "long_name" = "short_name") =>
    result.address_components?.find((c: any) => c.types.includes(componentType))?.[part] ?? null;

export type AddressData = {
  addressStreet?: string | null;
  addressLineTwo?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  addressCoordinates?: string | null;
  addressPlaceId?: string | null;
};

export const addressKeys = ["addressStreet", "addressLineTwo", "addressCity", "addressState", "addressZip", "addressPlaceId"];

export const addressFromGoogleResponse = (result: PlaceResult) => {
  const addr = getAddressComponent(result);
  return {
    addressStreet: [addr("street_number"), addr("route")]
      .filter(Boolean)
      .join(" "),
    addressCity: addr("locality", "long_name") || addr("sublocality", "long_name"),
    addressState: addr("administrative_area_level_1"),
    addressZip: addr("postal_code"),
    addressPlaceId: result.place_id || null,
  }
}


export function routeMarkers(
  provider: { addressCoordinates: string | null },
  shift: ShiftGeneric | undefined,
  visitResources: {
    __typename?: "VisitResourceSelectItem",
    startTime: string,
    addressCoordinates?: string | null,
    displayLabel?: string | null,
    resourceRequirement?: {
      inHome: boolean
    } | null,
    visit: {
      addressCoordinates: string,
      patient: {
        id: number
        firstName: string
        lastName: string
      } | null,
    } | null,
  }[],
  calendarBlocks: {
    __typename?: "CalendarBlockSelectItemWithFilters",
    startTime: string,
    addressCoordinates: string | null,
    title: string | null,
  }[],
  platform: 'web' | 'mobile' = 'web'
) {
  const markerInputList: MapMarkerInput[] = [];

  let visitsAtHomeCount = 0;

  const calendarEvents = [...visitResources, ...calendarBlocks];

  // Sort calendar events by start time to get correct schedule order
  const sortedCalendarEvents = calendarEvents.sort((a, b) =>
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  // Group visits by location (coordinates)
  const locationGroups = new Map<string, {
    patient: { id: number, firstName: string, lastName: string } | null,
    visits: {
      startTime: string,
      scheduleIndex: number,
      patient?: { id: number, firstName: string, lastName: string },
      title?: string
    }[],
    point: any,
  }>();


  sortedCalendarEvents.forEach((ce, idx) => {
    if (ce.__typename === "VisitResourceSelectItem") {
      if (!ce.visit?.patient) { return; }
      
      const visitLocation = getVisitResourceLocation(ce, provider, shift || {
        addressCoordinates: provider.addressCoordinates!,
        useShiftCenterDriveTimes: false,
        shiftCenter: null,
      });
      
      if (visitLocation == getShiftStartEndLocation(provider, shift || {
        addressCoordinates: provider.addressCoordinates!,
        useShiftCenterDriveTimes: false,
        shiftCenter: null,
      })) {
        visitsAtHomeCount += 1
      } else {
        const point = coordinateToMapPoint(visitLocation || "");
        if (point) {
          const locationKey = visitLocation;

          if (!locationGroups.has(locationKey)) {
            locationGroups.set(locationKey, {
              patient: null,
              visits: [],
              point
            });
          }

          locationGroups.get(locationKey)!.visits.push({
            startTime: ce.startTime,
            scheduleIndex: idx + 1,
            patient: ce.displayLabel ? undefined : ce.visit.patient,
            title: ce.displayLabel || undefined,
          });
        }
      }
    } else if (ce.__typename === "CalendarBlockSelectItemWithFilters") {
      const point = coordinateToMapPoint(ce.addressCoordinates!);
      if (point) {
        const locationKey = ce.addressCoordinates!;

        if (!locationGroups.has(locationKey)) {
          locationGroups.set(locationKey, {
            patient: null,
            visits: [],
            point
          });
        }

        locationGroups.get(locationKey)!.visits.push({
          startTime: ce.startTime,
          scheduleIndex: idx + 1,
          title: ce.title || "Calendar Block"
        });
      }
    }
  });

  locationGroups.forEach((group) => {
    group.visits.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    const isSingleVisit = group.visits.length === 1;
    const isMultipleVisits = group.visits.length > 1;

    const visitEntries: VisitEntry[] = group.visits.map(v => ({
      scheduleIndex: v.scheduleIndex,
      time: new Date(v.startTime).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      displayName: v.title
        ? v.title
        : v.patient
        ? `${v.patient.firstName} ${v.patient.lastName}`
        : 'Unknown',
      fullLabel: `${v.scheduleIndex}. ${v.title ? v.title : v.patient ? `${v.patient.firstName} ${v.patient.lastName}` : 'Unknown'}`,
      timestamp: new Date(v.startTime),
      patientId: v.patient?.id
    }));

    // WEB PLATFORM CASES
    if (platform === 'web') {
      
      // Case 1: Single visit → Single patient visit marker
      if (isSingleVisit) {
        const visit = group.visits[0];
        const displayName = visit.title
          ? visit.title
          : visit.patient
          ? `${visit.patient.firstName} ${visit.patient.lastName}`
          : 'Unknown';

        markerInputList.push({
          label: `${visit.scheduleIndex}. ${displayName}`,
          timestamp: new Date(visit.startTime),
          highlighted: new Date(visit.startTime) > new Date(),
          icon: "place",
          point: group.point,
          patient: visit.patient,
        });
      }
      
      // Case 2: Multiple visits → Custom HTML marker (duplicated for clustering)
      else if (isMultipleVisits) {
        const firstVisit = group.visits[0];
        const visitCount = group.visits.length;
        
        for(let i = 0; i < visitCount; i++) {
          markerInputList.push({
            label: "",
            timestamp: new Date(firstVisit.startTime),
            highlighted: group.visits.some(v => new Date(v.startTime) > new Date()),
            icon: "place",
            point: group.point,
            patient: undefined,
            visitEntries
          } as WebMarkerInput);
        }
      }
    }

    // MOBILE PLATFORM CASES  
    else if (platform === 'mobile') {
      
      // Case 3: Single visit → Mobile marker
      if (isSingleVisit) {
        markerInputList.push({
          point: group.point,
          visitEntries
        } as MobileMarkerInput);
      }
      
      // Case 4: Multiple visits → Mobile marker
      else if (isMultipleVisits) {
        markerInputList.push({
          point: group.point,
          visitEntries
        } as MobileMarkerInput);
      }
    }
  });

  if (shift) {
    // Only add marker for the first shift, since they will all be in the same place for now
    const startLocation = getShiftStartEndLocation(provider, shift);
    const homePoint = coordinateToMapPoint(startLocation);
    const label = visitsAtHomeCount ? `Start (+${visitsAtHomeCount} virtual visits)` : 'Start'
    if (homePoint) {
      markerInputList.push({
        label: label,
        timestamp: new Date(shift.startTime),
        highlighted: true,
        icon: "house",
        point: homePoint,
      } as WebMarkerInput);
    }
  }

  return markerInputList.sort((a, b) => {
    const aTime = 'timestamp' in a ? a.timestamp.getTime() : a.visitEntries[0].timestamp.getTime();
    const bTime = 'timestamp' in b ? b.timestamp.getTime() : b.visitEntries[0].timestamp.getTime();
    return aTime - bTime;
  });
}

export const emptyAddress = () => ({
  addressStreet: "",
  addressCity: "",
  addressState: "",
  addressZip: "",
});
